import * as React from 'react';
import { TitleNewsStatus } from '../../enums';
import { Grid2 as Grid } from '@mui/material';
import { Dropzone, Metadata, TextField, Button, NumberSelect, TextSelect, DateTimePicker } from '../common';
import { BaseDialog, BaseDialogProps, BaseDialogState } from '../common/dialog/BaseDialog';
import { default as moment, Moment } from 'moment';
import { DateTimeValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers';

interface Props extends BaseDialogProps {
    readonly segments: Entities.Segment[];
    readonly selectedTitleNews: Entities.TitleNews | null;
    readonly onUpdate: (id: string, date: number, endDate: number, status: TitleNewsStatus, title: string, body: string, file: any, metadata: string, segmentFilterId: string | null) => void;
}

interface State extends BaseDialogState {
    readonly date: Moment;
    readonly endDate: Moment;
    readonly status: TitleNewsStatus;
    readonly title: string;
    readonly body: string;
    readonly file: File | null;
    readonly imageUrl: string;
    readonly metadata: string;
    readonly segmentFilterId: string;
}

export class UpdateTitleNewsDialog extends BaseDialog<Props, State> {
    state = this.initState();

    componentDidUpdate(prevProps: Props) {
        const { selectedTitleNews, open } = this.props;
        if ( open && selectedTitleNews && !prevProps.selectedTitleNews) {
            this.setState(this.initState());
        }
    }
    protected renderContent(): React.JSX.Element {
        const { segments } = this.props;
        const { date, endDate, status, title, body, imageUrl, metadata, segmentFilterId } = this.state;
        const statusOptions: TitleNewsStatus[] = [
            TitleNewsStatus.None,
            TitleNewsStatus.Unpublished,
            TitleNewsStatus.Published,
            TitleNewsStatus.Archived,
        ];

        return (
            <Grid container={true} spacing={0}>
                <Grid size={12}>
                    <TextField
                        label="Title"
                        fullWidth={true}
                        value={title}
                        required={true}
                        onChange={this.updateTitle}
                        variant="outlined"
                    />
                </Grid>
                <Grid size={12}>
                    <DateTimePicker
                        label="Start"
                        value={date}
                        onChange={this.updateDate}
                        disablePast={false}
                    />
                </Grid>
                <Grid size={12}>
                    <DateTimePicker
                        label="End"
                        value={endDate}
                        onChange={this.updateEndDate}
                        disablePast={true}
                    />
                </Grid>
                <Grid size={12}>
                    <NumberSelect
                        label="Status"
                        value={status}
                        onChange={this.updateStatus}
                    >
                        {statusOptions.map(value => (
                            <option key={value} value={value}>
                                {`${TitleNewsStatus[value]}`}
                            </option>
                        ))}
                    </NumberSelect>
                </Grid>
                <Grid size={12}>
                    <Dropzone
                        label="Image"
                        accept={{
                            "image/jpeg": ['.jpeg'],
                            "image/jpg": ['.jpg'],
                            "image/png": ['.png'],
                        }}
                        preview={imageUrl}
                        multiple={false}
                        text={'Drop image file'}
                        description={'Upload image (JPEG, JPG or PNG)'}
                        onDropFile={this.handleDrop}
                    />
                </Grid>
                <Grid size={12}>
                    <Metadata
                        styled={this.props.styled}
                        label="Metadata"
                        text={metadata}
                        onChange={this.handleChangeMetadata}
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        label="Body"
                        value={body}
                        variant="outlined"
                        multiline={true}
                        rows={4}
                        fullWidth={true}
                        onChange={this.updateBody}
                    />
                </Grid>
                {segments && segments.length > 0 && (
                    <Grid size={12}>
                        <TextSelect
                            label="Filter Segment"
                            value={segmentFilterId}
                            onChange={this.updateSegmentFilterId}
                        >
                            <option key={'none'} value={'none'}>
                                {`None`}
                            </option>
                            {segments.map(segment => (
                                <option key={segment.id} value={segment.id}>
                                    ${segment.name}
                                </option>
                            ))}
                        </TextSelect>
                    </Grid>
                )}
            </Grid>
        );
    }

    protected renderActions(): React.JSX.Element {
        return (
            <Button text="Update" onClick={this.onUpdate} variant="contained" disabled={!this.validate()}/>
        );
    }

    protected initState(): State {
        const { selectedTitleNews } = this.props;
        if (!selectedTitleNews) {
            return {
                date: moment.parseZone(),
                endDate: moment.parseZone(),
                status: TitleNewsStatus.None,
                title: '',
                body: '',
                file: null,
                imageUrl: '',
                metadata: '',
                segmentFilterId: 'none',
            };
        }

        return {
            date: moment(selectedTitleNews.timestamp),
            endDate: moment(selectedTitleNews.endTimestamp),
            status: selectedTitleNews.status,
            title: selectedTitleNews.title,
            body: selectedTitleNews.body,
            file: null,
            imageUrl: selectedTitleNews.imageUrl,
            metadata: selectedTitleNews.metadata,
            segmentFilterId: selectedTitleNews.segmentFilterId !== '' ? selectedTitleNews.segmentFilterId : 'none',
        };
    }

    private updateDate = (date: Moment | null, context: PickerChangeHandlerContext<DateTimeValidationError>) => {
        if (date === null) {
            return;
        }
        this.setState({ date });
    }

    private updateEndDate = (endDate: Moment | null, context: PickerChangeHandlerContext<DateTimeValidationError>) => {
        if (endDate === null) {
            return;
        }
        this.setState({ endDate });
    }

    private updateStatus = (status: number) => this.setState({ status });

    private updateSegmentFilterId = (segmentFilterId: string) => this.setState({ segmentFilterId });

    private updateTitle = (title: string) => this.setState({ title });

    private updateBody = (body: string) => this.setState({ body });

    private handleDrop = (file: File) => this.setState({ file });

    private handleChangeMetadata = (metadata: string) => this.setState({ metadata });

    private validate(): boolean {
        if (!this.validateStatus()) {
            return false;
        }

        const { date, endDate, title, body } = this.state;
        return endDate.isAfter(date) && title !== '' && body !== '';
    }

    private validateStatus(): boolean {
        const { status } = this.state;
        return status !== TitleNewsStatus.None;

    }

    private onUpdate = () => {
        if (!this.validateStatus()) {
            return;
        }
        const { selectedTitleNews } = this.props;
        if (!selectedTitleNews) {
            return;
        }

        const { newsId } = selectedTitleNews;
        const { date, endDate, status, title, body, file, metadata, segmentFilterId } = this.state;
        const dateValue = date.utc().valueOf();
        const endDateValue = endDate.utc().valueOf();
        const segmentId = segmentFilterId !== 'none' ? segmentFilterId : null;
        this.props.onUpdate(newsId, dateValue, endDateValue, status, title, body, file, metadata, segmentId);
        this.onClose();
    };
}
